/* eslint-disable no-unused-vars */
/* global React */
import { useAttachmentAPI, useMemoRef, useStyles } from '@platform/react/hook'
import { Grid } from '@mui/material'
import Text from 'ui/Element/Text'
import FileImage from 'ui/Element/Attachment/Item/File'
import { useMemo } from 'react'

/**
 * Returns the AttachmentListTemplateLine element's current theme styles.
 *
 * @param {Object} theme  the application's current theme
 * @returns {Object}      the AttachmentListTemplateLine component's styles
 */
const styles = theme => ({
  root: {
    padding: theme.spacing(1, 0),
    alignItems: 'stretch',
    width: 0,
  },
  image: {
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left',
    backgroundSize: 'contain',
  },
  text: {
    display: 'inline-block',
    whiteSpace: [['nowrap'], '!important'],
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  ...theme.custom.attachmentListTemplateLine,
})

/**
 * AttachmentListTemplateLine attachment component
 *
 * Displays a small representation of the file together with its name as a line.
 *
 * @param {Object} attachment the attached file
 * @param {Object} types      the types for the {@link FileImage} element
 * @param {boolean} download  whether the file can be downloaded
 * @param {Object} events     events for the component
 * @param {string} [group]    attachment group
 * @param group
 */
const AttachmentListTemplateLine = ({ attachment, types, download = true, events, group }) => {
  const classes = useStyles(styles)()
  const { value: item } = attachment
  const sanitizedName = useMemo(() => decodeURIComponent(item.name), [item.name])

  return (
    <Grid
      item
      container
      className={classes.root}
      title={sanitizedName}
      xs={12}
      md={12}
      xl={12}
    >
      <FileImage
        types={types}
        group={group}
        attachment={attachment}
        download={download}
        // This component isn't meant to preview (meaning to actually render the contents of the
        // attachment) anything else than images. For other file types, we'll show a placeholder
        // So it isn't necessary to make this configurable
        previewableTypes={['image']}
        events={{ onClick: events?.onClick }}
        md={1}
        lg={1}
        xl={1}
      />
      <Grid
        item
        container
        component={Text}
        className={classes.text}
        color={'textSecondary'}
        alignContent={'center'}
        xs={11}
        md={11}
        xl={11}
      >
        {sanitizedName}
      </Grid>
    </Grid>
  )
}

export default useMemoRef(AttachmentListTemplateLine, props => [props.attachment])
